import React from "react";
import { FormLabel, FormSelect, Col, FormControl, FormGroup, FormCheck } from "react-bootstrap";
import Select from "react-select";
import { FileUpload } from "../FileUpload/FileUpload";
import { formInterface } from "./formInterface";
import "./form.css";

interface FormProps {
    field: formInterface;
    onChange: (key: string, value: any) => void;
    data: any;
    altData?: any
    condition?: any;
}

export const Form: React.FC<FormProps> = ({ altData, field, onChange, data, condition }) => {

    const renderLabel = (f: formInterface) => {
        if (!f.name && !f.require) return null;
        return (
            <FormLabel className="generic-form-label">
                {f.name}
                {f.require && <span className="label-required">*</span>}
            </FormLabel>
        );
    };

    const colProps = {
        xs: 12,
        sm: 12,
        md: field.md || 6,
        className: "generic-form-col mb-4"
    };

    const formGroupClass = "generic-form-group";
    const formControlClass = "generic-form-control";

    const [touched, setTouched] = React.useState(false);
    const [searchText, setSearchText] = React.useState("");
    const isEmpty = field.require && (data === undefined || data === null || (typeof data === 'string' && String(data).trim() === ''));
    const showError = touched && isEmpty;

    const handleChange = (e: any) => {
        const value = e.target.value;
        onChange(field.key, value);
    };

    const handleBlur = () => {
        setTouched(true);
    };

    let inputElement;

    switch (field.type) {
        case "password":
            inputElement = (
                <FormControl
                    type="password"
                    required={field.require}
                    isInvalid={showError}
                    onBlur={handleBlur}
                    onInvalid={(e: any) => {
                        e.preventDefault();
                        setTouched(true);
                    }}
                    className={formControlClass}
                    placeholder={field.placeholder || ''}
                    value={data || ''}
                    onChange={handleChange}
                />
            );
            break;

        case "number":
            inputElement = (
                <FormControl
                    type="number"
                    required={field.require}
                    isInvalid={showError}
                    onBlur={handleBlur}
                    onInvalid={(e: any) => {
                        e.preventDefault();
                        setTouched(true);
                    }}
                    className={formControlClass}
                    placeholder={field.placeholder || ''}
                    value={data || ''}
                    onChange={handleChange}
                />
            );
            break;

        case "select":
            inputElement = (
                <FormSelect
                    required={field.require}
                    isInvalid={showError}
                    onBlur={handleBlur}
                    onInvalid={(e: any) => {
                        e.preventDefault();
                        setTouched(true);
                    }}
                    onChange={handleChange}
                    className={`${formControlClass} generic-form-select`}
                >
                    <option value="">{field.placeholder || "Please Select"}</option>
                    {field.options?.map((option: any, idx) => {
                        const val = typeof option === "object" ? option.value : option;
                        const label = typeof option === "object" ? option.label : option;
                        if (String(val) == String(altData)) {
                            return <option key={idx} value={val} selected={true}>{label}</option>;
                        }
                        else if (String(data) == String(val)) {
                            return <option key={idx} value={val} selected={true}>{label}</option>;
                        }
                        else {
                            return <option key={idx} value={val}>{label}</option>;
                        }
                    })}
                </FormSelect>
            );
            break;

        case "multi-select":
            const selectOptions = field.options?.map((option: any) => {
                const val = typeof option === "object" ? String(option.value) : String(option);
                const label = typeof option === "object" ? option.label : option;
                return { value: val, label: label };
            }) || [];
            
            const selectedValues = Array.isArray(data) 
                ? selectOptions.filter(opt => data.includes(opt.value))
                : [];

            inputElement = (
                <Select
                    isMulti
                    options={selectOptions}
                    value={selectedValues}
                    onChange={(selected: any) => {
                        const values = selected ? selected.map((item: any) => item.value) : [];
                        onChange(field.key, values);
                    }}
                    onBlur={handleBlur}
                    placeholder={field.placeholder || 'Search and select...'}
                    classNamePrefix="react-select"
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                />
            );
            break;

        case "email":
            inputElement = (
                <FormControl
                    type="email"
                    required={field.require}
                    isInvalid={showError}
                    onBlur={handleBlur}
                    onInvalid={(e: any) => {
                        e.preventDefault();
                        setTouched(true);
                    }}
                    className={formControlClass}
                    placeholder={field.placeholder || ''}
                    value={data || ''}
                    onChange={handleChange}
                />
            );
            break;

        case "textarea":
            inputElement = (
                <FormControl
                    as="textarea"
                    rows={3}
                    required={field.require}
                    isInvalid={showError}
                    onBlur={handleBlur}
                    onInvalid={(e: any) => {
                        e.preventDefault();
                        setTouched(true);
                    }}
                    className={formControlClass}
                    placeholder={field.placeholder || ''}
                    value={data || ''}
                    onChange={handleChange}
                />
            );
            break;

        case "fileUpload":
            inputElement = (
                <FileUpload
                    onChange={onChange}
                    fieldKey={field.key}
                    value={data}
                />
            );
            break;

        case "Date":
            inputElement = (
                <FormControl
                    type="date"
                    required={field.require}
                    isInvalid={showError}
                    onBlur={handleBlur}
                    onInvalid={(e: any) => {
                        e.preventDefault();
                        setTouched(true);
                    }}
                    className={formControlClass}
                    value={data || ''}
                    onChange={handleChange}
                />
            );
            break;

        case "radio":
            inputElement = (
                <div className="generic-form-radio-group">
                    {field.options?.map((option: any, idx) => {
                        const val = typeof option === "object" ? option.value : option;
                        const label = typeof option === "object" ? option.label : option;
                        const isChecked = data === undefined ? val === false : String(data) === String(val);
                        return (
                            <FormCheck
                                key={idx}
                                type="radio"
                                id={`${field.key}-${val}`}
                                name={field.key}
                                label={label}
                                checked={isChecked}
                                onChange={() => onChange(field.key, val)}
                            />
                        );
                    })}
                </div>
            );
            break;

        default:
            inputElement = (
                <FormControl
                    type="text"
                    required={field.require}
                    isInvalid={showError}
                    onBlur={handleBlur}
                    onInvalid={(e: any) => {
                        e.preventDefault();
                        setTouched(true);
                    }}
                    className={formControlClass}
                    value={data || ''}
                    placeholder={field.placeholder || ''}
                    onChange={handleChange}
                />
            );
    }

    if (field.condition && field.key !== condition) {
        return (
            <FormControl
                type="hidden"
                hidden
                value={data || ''}
                onChange={handleChange}
            />
        );
    }

    return (
        <Col {...colProps}>
            <FormGroup controlId={field.key} className={formGroupClass}>
                {renderLabel(field)}
                {inputElement}
                {showError && <div className="text-danger small mt-1">{field.name || 'This field'} is required</div>}
            </FormGroup>
        </Col>
    );
};